import type { CellClassParams, ColDef } from 'ag-grid-community';
import { findIndex, isNil } from 'lodash';
import type { Analysis, NamedEntity, ReturnCell } from 'venn-api';
import type { StudioAnalysisRequest } from 'venn-state';
import { type CustomBlockTypeEnum, type CustomizableMetric, PRIVATES_PERFORMANCE_BLOCKS } from 'venn-utils';
import {
  BOLD_CLASS,
  GREY_BG_CLASS,
  LEFT_ALIGN_CLASS,
  METADATA_GREY_BG_CLASS,
  ROW_HAS_ERROR_CLASS,
} from '../../studio-blocks/customAnalysisContants';
import { type AnalysisRequest } from '../../studio-blocks/types';
import type { ReturnsGridRows } from '../../studio-blocks/components/blocks/returns-grid/useReturnsGridData';

const shouldKeepRequest = (
  req: StudioAnalysisRequest | undefined,
  isTreeBlock: boolean,
  hideBenchmark: boolean | undefined,
) => req && !(isTreeBlock && req.subject.subjectType === 'INVESTMENT') && !(req.isBenchmark && hideBenchmark);

export const filterRequests = (allRequests: AnalysisRequest[], isTreeBlock: boolean, hideBenchmark?: boolean) => {
  return allRequests.filter((request) => shouldKeepRequest(request, isTreeBlock, hideBenchmark));
};

export const filterAnalyses = (
  allRequests: AnalysisRequest[],
  allAnalyses: (Analysis | undefined)[][],
  isTreeBlock: boolean,
  hideBenchmark?: boolean,
) => {
  const filterPredicate = (_foo: unknown, index: number) =>
    shouldKeepRequest(allRequests[index], isTreeBlock, hideBenchmark);
  return allAnalyses.filter(filterPredicate);
};

// Column overrides for tree grid blocks
export const treeGridColDefOverrides: ColDef = {
  cellClassRules: {
    [BOLD_CLASS]: (params: CellClassParams) => params.data.isStrategy,
    [GREY_BG_CLASS]: (params: CellClassParams) => params.data.isMetadata,
    [LEFT_ALIGN_CLASS]: (params: CellClassParams) => typeof params.value === 'string',
  },
};

// Column overrides for analysis grid blocks
export const analysisGridDefaultColDefOverrides: ColDef<ReturnsGridRows> = {
  cellClassRules: {
    [METADATA_GREY_BG_CLASS]: ({ data, colDef }: CellClassParams) =>
      data?.isMetadata || colDef.cellRendererParams?.isMetadata,
    [LEFT_ALIGN_CLASS]: (params: CellClassParams) => typeof params.value === 'string',
    [ROW_HAS_ERROR_CLASS]: ({ data }: CellClassParams) => data?.hasError,
  },
};

export const shouldHideBenchmark = (
  customBlockType: CustomBlockTypeEnum | undefined,
  metricDefinitionMap: Record<string, CustomizableMetric>,
  selectedMetrics: string[],
) => {
  if (isNil(customBlockType) || !PRIVATES_PERFORMANCE_BLOCKS.includes(customBlockType)) {
    return false;
  }

  // If none of the selected metrics need benchmark (e.g. only called % and RVPI % are selected)
  // then we will hide benchmark even if it's selected in block settings because it has no effect on these metrics
  return selectedMetrics
    .map((metric) => metricDefinitionMap[metric]!)
    .every((metricDefinition) => metricDefinition.needsBenchmark !== true);
};

export const isExportable = (exportable: boolean[], reqs: AnalysisRequest[], seriesName: NamedEntity) => {
  const index = findIndex(reqs, (req) => req.subject.id === String(seriesName.id));
  if (index === -1) {
    return false;
  }
  return exportable[index];
};

export const convertToTimeseries = (ts: number[][] | ReturnCell[]): number[][] => {
  if (!ts) {
    return [];
  }
  if (Array.isArray(ts[0])) {
    return ts as number[][];
  }

  return (ts as ReturnCell[]).filter((cell) => cell.cellTypes.includes('ANALYSIS')).map((t) => [t.date, t.val]);
};
